/**
 * Turns AI-usage rows into pie slices.
 *
 * Pure on purpose: this is the only part of the chart that can be wrong, and
 * keeping it out of the component means it can be tested without rendering
 * Recharts (whose ResponsiveContainer draws nothing at jsdom's 0x0).
 *
 * @module components/admin/aiUsageSlices
 */

import { colorAt, NEUTRAL_SLICE_COLOR } from '../charts/chartPalette';
import type { AiUsageRow } from '../../services/aiUsageService';

/** Beyond this many slices a pie stops being readable. */
const MAX_SLICES = 7;

export const OTHERS_LABEL = 'อื่นๆ';

export interface UsageSlice {
  label: string;
  value: number;
  color: string;
}

export interface SliceResult {
  slices: UsageSlice[];
  /** Sum of what was drawn — never of what was discarded. */
  total: number;
}

/**
 * @param rows   one dimension's rows, in any order.
 * @param unit   counting word for the folded bucket ("คน", "workspace").
 */
export const toSlices = (rows: AiUsageRow[], unit: string): SliceResult => {
  // A zero-token row cannot be drawn as an arc. It stays in the table, which is
  // the record; the chart only claims to show where the tokens went.
  const spent = rows
    .filter((r) => r.totalTokens > 0)
    .sort((a, b) => b.totalTokens - a.totalTokens);

  const total = spent.reduce((sum, r) => sum + r.totalTokens, 0);

  if (spent.length <= MAX_SLICES) {
    return {
      slices: spent.map((r, i) => ({ label: r.label, value: r.totalTokens, color: colorAt(i) })),
      total,
    };
  }

  const head = spent.slice(0, MAX_SLICES - 1);
  const tail = spent.slice(MAX_SLICES - 1);

  return {
    slices: [
      ...head.map((r, i) => ({ label: r.label, value: r.totalTokens, color: colorAt(i) })),
      {
        label: `${OTHERS_LABEL} (${tail.length} ${unit})`,
        value: tail.reduce((sum, r) => sum + r.totalTokens, 0),
        color: NEUTRAL_SLICE_COLOR,
      },
    ],
    total,
  };
};
